/**
 * Exporta conversas de chat para PDF com layout oficial Ferrugini.
 * TAREFA 1.3 — Marca d'água, logo e rodapé dentro do documento (Adendo A1).
 * Múltiplas páginas; template compacto para maximizar área do relatório.
 */
import { jsPDF } from "jspdf";
import logoHorizontal from "@/shared/assets/logo-horizontal.png";

export interface ChatMessageForPdf {
  role: "user" | "assistant";
  content: string;
}

const FERRUGINI_FOOTER =
  "Rua Henrique Moscoso, 1019 — Centro, Vila Velha/ES — (27) 3229-4434 — www.ferruginiadvogados.com.br";

/** Converte tabela markdown em linhas legíveis para PDF (uma linha por célula ou row compacta) */
function formatTableForPdf(tableMatch: string): string {
  const rows = tableMatch.trim().split("\n").filter(Boolean);
  if (rows.length < 2) return tableMatch;
  const lines: string[] = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (/^[\s\-:|]+$/.test(row.replace(/\|/g, ""))) continue; // separador
    const cells = row.split("|").map((c) => c.trim()).filter(Boolean);
    if (cells.length > 1) {
      lines.push(cells.join("  |  "));
    }
  }
  return lines.join("\n");
}

/** Remove markdown para exportação em texto/PDF */
function stripMarkdownForExport(markdown: string): string {
  let out = markdown
    .replace(/📄\s*/g, "Anexo: ")
    .replace(/\?\?\s*/g, "Anexo: ")
    .replace(/```[\s\S]*?```/g, (m) => m.replace(/```\w*\n?/g, "").replace(/```/g, ""))
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/^#+\s+/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  // Converter tabelas markdown em formato legível
  const tableRegex = /\|[^\n]+\|\n(?:\|[\s\-:|]+\|\n)?(?:\|[^\n]+\|\n?)*/g;
  out = out.replace(tableRegex, formatTableForPdf);
  return out;
}

export interface ExportChatToPdfOptions {
  /** Título principal (ex: "Conversa com Cleo", "Oráculo") */
  title: string;
  /** Subtítulo opcional (ex: "Instituição: X", "Data: ...") */
  subtitle?: string;
  /** Nome do assistente para rótulos (ex: "Cleo", "Oráculo", "IA Financeira") */
  assistantLabel?: string;
  /** Nome do arquivo (sem extensão) */
  filenamePrefix?: string;
}

/**
 * Exporta mensagens de chat para PDF com layout oficial Ferrugini.
 * Logo e rodapé compactos; marca d'água discreta; múltiplas páginas com paginação correta.
 */
export async function exportChatToPdfOfficial(
  messages: ChatMessageForPdf[],
  options: ExportChatToPdfOptions
): Promise<void> {
  const { title, subtitle, assistantLabel = "Assistente", filenamePrefix = "conversa" } = options;

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const maxWidth = pageWidth - margin * 2;
  const lineHeight = 5.5;

  // Template compacto: menos espaço para logo/rodapé, mais para o relatório
  const logoHeight = 8;
  const logoWidth = 36;
  const headerFirstPage = 28; // altura do cabeçalho na primeira página (logo + título)
  const headerContinuation = 12; // cabeçalho mínimo em páginas seguintes (só logo pequena)
  const footerHeight = 10;
  const footerTop = pageHeight - footerHeight;
  const contentBottom = footerTop - 4;

  const addHeader = (logoBase64: string, compact = false) => {
    if (!logoBase64) return;
    const w = compact ? logoWidth * 0.7 : logoWidth;
    const h = compact ? logoHeight * 0.7 : logoHeight;
    doc.addImage(logoBase64, "PNG", (pageWidth - w) / 2, compact ? 4 : 8, w, h);
  };

  const addFooter = () => {
    doc.setFillColor(121, 22, 20);
    doc.rect(0, footerTop, pageWidth, footerHeight, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.text(FERRUGINI_FOOTER, pageWidth / 2, footerTop + 6, { align: "center" });
    doc.setTextColor(0, 0, 0);
  };

  const addWatermark = () => {
    doc.setTextColor(235, 235, 235);
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.text("DOCUMENTO OFICIAL - FERRUGINI ADVOGADOS", pageWidth / 2, pageHeight / 2, {
      align: "center",
      angle: -30,
    });
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
  };

  let logoBase64: string;
  try {
    const resp = await fetch(logoHorizontal);
    const blob = await resp.blob();
    logoBase64 = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  } catch {
    logoBase64 = "";
  }

  const addNewPage = (pageNum: number) => {
    addFooter();
    doc.addPage();
    addHeader(logoBase64, pageNum > 1);
    addWatermark();
  };

  let pageNum = 1;
  let y = headerFirstPage + 4;

  // Primeira página: título e metadados compactos
  if (logoBase64) addHeader(logoBase64);
  addWatermark();

  doc.setFontSize(11);
  doc.setTextColor(60, 60, 60);
  doc.text(title, margin, y);
  y += 5;
  doc.setFontSize(8);
  if (subtitle) {
    doc.text(subtitle, margin, y);
    y += 4;
  }
  doc.text(`Data: ${new Date().toLocaleString("pt-BR")}`, margin, y);
  y += 8;
  doc.setTextColor(0, 0, 0);

  const contentTopFirst = y;
  const contentTopContinuation = headerContinuation + 6;

  /** Escreve linhas com paginação automática */
  const writeLines = (lines: string[]) => {
    const contentTop = pageNum === 1 ? contentTopFirst : contentTopContinuation;
    for (const line of lines) {
      if (y > contentBottom - lineHeight) {
        addNewPage(++pageNum);
        y = contentTopContinuation;
      }
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text(line, margin, y);
      y += lineHeight;
    }
  };

  for (const msg of messages) {
    const contentTop = pageNum === 1 ? contentTopFirst : contentTopContinuation;
    if (y > contentBottom - lineHeight * 2) {
      addNewPage(++pageNum);
      y = contentTopContinuation;
    }
    doc.setFont("helvetica", msg.role === "user" ? "bold" : "normal");
    doc.setFontSize(9);
    const label = msg.role === "user" ? "Você:" : `${assistantLabel}:`;
    doc.text(label, margin, y);
    y += lineHeight;

    const plain = stripMarkdownForExport(msg.content);
    const lines = doc.splitTextToSize(plain, maxWidth);
    writeLines(lines);
    y += 4;
  }

  addFooter();

  const safeName = filenamePrefix.replace(/[^a-zA-Z0-9\u00C0-\u024F\s-]/g, "").slice(0, 40) || "conversa";
  doc.save(`${safeName}-${Date.now()}.pdf`);
}
